import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getSeekerProfile,
  createOrUpdateSeekerProfile,
} from "../services/seekerProfileService";
import {
  getCompanyProfileByUserId,
  updateCompanyProfile,
} from "../services/companyService";
import { getUserData, getAllUsers } from "../services/userService";
import LoadingSpinner from "../components/LoadingSpinner";

// Bilingual Label Component
const BilingualLabel = ({ english, marathi, className = "" }) => (
  <label className={`text-sm font-medium text-gray-700 ${className}`}>
    {english} <span className="text-gray-500">/ {marathi}</span>
  </label>
);

const TeamUsers = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (userId) {
      fetchUserProfile();
    } else {
      fetchAllUsers();
    }
  }, [userId]);

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const result = await getAllUsers();
      if (result.success) {
        setUsers(result.users || []);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      // Fetch user data
      const userDataResult = await getUserData(userId);
      if (userDataResult.success && userDataResult.data) {
        setUserData(userDataResult.data);

        // Fetch profile based on user type
        if (userDataResult.data.userType === "jobseeker") {
          const result = await getSeekerProfile(userId);
          if (result.success && result.profile) {
            setProfile(result.profile);
            setEditedProfile(result.profile);
          }
        } else if (userDataResult.data.userType === "employer") {
          const result = await getCompanyProfileByUserId(userId);
          if (result.success && result.data) {
            setProfile(result.data);
            setEditedProfile(result.data);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (userData.userType === "jobseeker") {
        const result = await createOrUpdateSeekerProfile(userId, editedProfile);
        if (result.success) {
          setProfile(editedProfile);
          setIsEditing(false);
          alert("Profile updated successfully!");
        } else {
          alert("Failed to update profile: " + result.error);
        }
      } else if (userData.userType === "employer") {
        const result = await updateCompanyProfile(userId, editedProfile);
        if (result.success) {
          setProfile(editedProfile);
          setIsEditing(false);
          alert("Company profile updated successfully!");
        } else {
          alert("Failed to update profile: " + result.error);
        }
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      alert("Error saving profile");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setEditedProfile((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  if (loading) return <LoadingSpinner />;

  // If no userId, show users list
  if (!userId) {
    const filteredUsers = users.filter(
      (user) =>
        user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userType?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (date) => {
      if (!date) return 'N/A';
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleDateString();
    };

    const handleCardClick = (user) => {
      navigate(`/team/users/${user.id}`);
    };

    return (
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Manage Users</h1>
          <p className="text-gray-600">View and manage all users in the system</p>
        </div>

        {/* Search Bar */}
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <div className="relative w-full md:w-64">
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Users List */}
        <div className="space-y-6">
          {filteredUsers.map((user) => (
            <div 
              key={user.id} 
              className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden cursor-pointer hover:shadow-2xl transition-shadow"
              onClick={() => handleCardClick(user)}
            >
              {/* Enhanced Card Format for All Users */}
              <div className="p-6 bg-gradient-to-br from-green-50 via-white to-blue-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Avatar with gradient */}
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl flex-shrink-0 shadow-lg ring-4 ${
                      user.userType === 'jobseeker' ? 'bg-gradient-to-br from-blue-500 to-purple-600 ring-blue-100' :
                      user.userType === 'employer' ? 'bg-gradient-to-br from-green-500 to-teal-600 ring-green-100' :
                      user.userType === 'agent' ? 'bg-gradient-to-br from-purple-500 to-pink-600 ring-purple-100' :
                      'bg-gradient-to-br from-cyan-500 to-blue-600 ring-cyan-100'
                    }`}>
                      {user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Name and User Type Badge */}
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-bold text-gray-900">
                          {user.firstName} {user.lastName}
                        </h3>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm ${
                          user.userType === 'employer' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' :
                          user.userType === 'jobseeker' ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' :
                          user.userType === 'agent' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' :
                          user.userType === 'team' ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>
                          {user.userType === 'employer' ? 'Company' : user.userType?.charAt(0).toUpperCase() + user.userType?.slice(1)}
                        </span>
                      </div>

                      {/* Joined Date */}
                      <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        Joined: {formatDate(user.createdAt)}
                      </p>

                      {/* Contact Info */}
                      <div className="grid grid-cols-3 gap-3 pt-3 mt-3 border-t-2 border-gray-200">
                        <div className="bg-blue-50 rounded-lg p-2">
                          <p className="text-xs font-medium text-blue-600 mb-1">Email</p>
                          <p className="text-xs text-gray-700 font-medium truncate">{user.email}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-2">
                          <p className="text-xs font-medium text-green-600 mb-1">Phone</p>
                          <p className="text-xs text-gray-700 font-medium">{user.phone || 'N/A'}</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-2">
                          <p className="text-xs font-medium text-purple-600 mb-1">User ID</p>
                          <p className="text-xs text-gray-600 font-mono truncate" title={user.id}>{user.id.substring(0, 10)}...</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* View Profile Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/team/users/${user.id}`);
                    }}
                    className="ml-4 px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span className="text-sm">View Profile</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // If userId exists but no userData, show not found
  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            User Not Found
          </h2>
          <button
            onClick={() => navigate("/team/users")}
            className="text-blue-600 hover:underline"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/team/users")}
                className="text-gray-600 hover:text-gray-900"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {userData.firstName} {userData.lastName}
                </h1>
                <p className="text-sm text-gray-500">
                  {userData.userType?.charAt(0).toUpperCase() +
                    userData.userType?.slice(1)}{" "}
                  Profile
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditedProfile(profile);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            User Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="text-gray-900">{userData.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Phone</label>
              <p className="text-gray-900">{userData.phone || "N/A"}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                User ID
              </label>
              <p className="text-gray-900 font-mono text-sm">{userId}</p>
            </div>
          </div>
        </div>

        {/* Job Seeker Profile */}
        {userData.userType === "jobseeker" && profile && (
          <div className="space-y-6">
            {/* Personal Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Personal Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <BilingualLabel english="First Name" marathi="पहिले नाव" />
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.personalInfo?.firstName || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "personalInfo",
                          "firstName",
                          e.target.value
                        )
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter first name / पहिले नाव प्रविष्ट करा"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.personalInfo?.firstName || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <BilingualLabel english="Last Name" marathi="आडनाव" />
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.personalInfo?.lastName || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "personalInfo",
                          "lastName",
                          e.target.value
                        )
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter last name / आडनाव प्रविष्ट करा"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.personalInfo?.lastName || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <BilingualLabel english="Gender" marathi="लिंग" />
                  {isEditing ? (
                    <select
                      value={editedProfile?.personalInfo?.gender || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "personalInfo",
                          "gender",
                          e.target.value
                        )
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select gender / लिंग निवडा</option>
                      <option value="Male">Male / पुरुष</option>
                      <option value="Female">Female / स्त्री</option>
                      <option value="Other">Other / इतर</option>
                    </select>
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.personalInfo?.gender || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <BilingualLabel english="Date of Birth" marathi="जन्मतारीख" />
                  {isEditing ? (
                    <input
                      type="date"
                      value={editedProfile?.personalInfo?.dateOfBirth || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "personalInfo",
                          "dateOfBirth",
                          e.target.value
                        )
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.personalInfo?.dateOfBirth || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <BilingualLabel english="Location" marathi="स्थान" />
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.personalInfo?.location || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "personalInfo",
                          "location",
                          e.target.value
                        )
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter location / स्थान प्रविष्ट करा"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.personalInfo?.location || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <BilingualLabel english="LinkedIn" marathi="लिंक्डइन" />
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.personalInfo?.linkedIn || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "personalInfo",
                          "linkedIn",
                          e.target.value
                        )
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="LinkedIn profile URL"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.personalInfo?.linkedIn || "N/A"}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <BilingualLabel english="Portfolio" marathi="पोर्टफोलिओ" />
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.personalInfo?.portfolio || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "personalInfo",
                          "portfolio",
                          e.target.value
                        )
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Portfolio website URL"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.personalInfo?.portfolio || "N/A"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Professional Summary */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Professional Summary{" "}
                <span className="text-gray-500">/ व्यावसायिक सारांश</span>
              </h2>
              {isEditing ? (
                <textarea
                  value={editedProfile?.professionalSummary || ""}
                  onChange={(e) =>
                    setEditedProfile((prev) => ({
                      ...prev,
                      professionalSummary: e.target.value,
                    }))
                  }
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter professional summary / व्यावसायिक सारांश प्रविष्ट करा..."
                />
              ) : (
                <p className="text-gray-900">
                  {profile.professionalSummary || "N/A"}
                </p>
              )}
            </div>

            {/* Experience */}
            {profile?.experience && profile.experience.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Experience <span className="text-gray-500">/ अनुभव</span>
                </h2>
                <div className="space-y-4">
                  {profile.experience.map((exp, index) => (
                    <div
                      key={index}
                      className="border-l-4 border-blue-500 pl-4 py-2"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-500">
                            Job Title / नोकरीचे शीर्षक
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={
                                editedProfile?.experience?.[index]?.jobTitle ||
                                ""
                              }
                              onChange={(e) => {
                                const newExp = [
                                  ...(editedProfile.experience || []),
                                ];
                                newExp[index] = {
                                  ...newExp[index],
                                  jobTitle: e.target.value,
                                };
                                setEditedProfile((prev) => ({
                                  ...prev,
                                  experience: newExp,
                                }));
                              }}
                              className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="font-semibold text-gray-900">
                              {exp.jobTitle}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500">
                            Company / कंपनी
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={
                                editedProfile?.experience?.[index]?.company ||
                                ""
                              }
                              onChange={(e) => {
                                const newExp = [
                                  ...(editedProfile.experience || []),
                                ];
                                newExp[index] = {
                                  ...newExp[index],
                                  company: e.target.value,
                                };
                                setEditedProfile((prev) => ({
                                  ...prev,
                                  experience: newExp,
                                }));
                              }}
                              className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="text-gray-600">{exp.company}</p>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        {exp.startDate} -{" "}
                        {exp.isCurrentJob ? "Present" : exp.endDate}
                        {exp.yearsOfExperience &&
                          ` • ${exp.yearsOfExperience} years`}
                      </p>
                      {exp.description && (
                        <p className="text-sm text-gray-700 mt-2">
                          {exp.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education */}
            {profile?.education && profile.education.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Education <span className="text-gray-500">/ शिक्षण</span>
                </h2>
                <div className="space-y-4">
                  {profile.education.map((edu, index) => (
                    <div
                      key={index}
                      className="border-l-4 border-green-500 pl-4 py-2"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-500">
                            Degree / पदवी
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={
                                editedProfile?.education?.[index]?.degree || ""
                              }
                              onChange={(e) => {
                                const newEdu = [
                                  ...(editedProfile.education || []),
                                ];
                                newEdu[index] = {
                                  ...newEdu[index],
                                  degree: e.target.value,
                                };
                                setEditedProfile((prev) => ({
                                  ...prev,
                                  education: newEdu,
                                }));
                              }}
                              className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="font-semibold text-gray-900">
                              {edu.degree}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500">
                            Institution / संस्था
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={
                                editedProfile?.education?.[index]
                                  ?.institution || ""
                              }
                              onChange={(e) => {
                                const newEdu = [
                                  ...(editedProfile.education || []),
                                ];
                                newEdu[index] = {
                                  ...newEdu[index],
                                  institution: e.target.value,
                                };
                                setEditedProfile((prev) => ({
                                  ...prev,
                                  education: newEdu,
                                }));
                              }}
                              className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="text-gray-600">{edu.institution}</p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500">
                            Field of Study / अभ्यासाचे क्षेत्र
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={
                                editedProfile?.education?.[index]
                                  ?.fieldOfStudy || ""
                              }
                              onChange={(e) => {
                                const newEdu = [
                                  ...(editedProfile.education || []),
                                ];
                                newEdu[index] = {
                                  ...newEdu[index],
                                  fieldOfStudy: e.target.value,
                                };
                                setEditedProfile((prev) => ({
                                  ...prev,
                                  education: newEdu,
                                }));
                              }}
                              className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="text-gray-600">{edu.fieldOfStudy}</p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500">
                            Graduation Year / पदवी वर्ष
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={
                                editedProfile?.education?.[index]
                                  ?.graduationYear || ""
                              }
                              onChange={(e) => {
                                const newEdu = [
                                  ...(editedProfile.education || []),
                                ];
                                newEdu[index] = {
                                  ...newEdu[index],
                                  graduationYear: e.target.value,
                                };
                                setEditedProfile((prev) => ({
                                  ...prev,
                                  education: newEdu,
                                }));
                              }}
                              className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="text-gray-600">
                              {edu.graduationYear || edu.year}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500">
                            Grade / श्रेणी
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={
                                editedProfile?.education?.[index]?.grade || ""
                              }
                              onChange={(e) => {
                                const newEdu = [
                                  ...(editedProfile.education || []),
                                ];
                                newEdu[index] = {
                                  ...newEdu[index],
                                  grade: e.target.value,
                                };
                                setEditedProfile((prev) => ({
                                  ...prev,
                                  education: newEdu,
                                }));
                              }}
                              className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="text-gray-600">
                              {edu.grade || "N/A"}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500">
                            Location / स्थान
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={
                                editedProfile?.education?.[index]?.location ||
                                ""
                              }
                              onChange={(e) => {
                                const newEdu = [
                                  ...(editedProfile.education || []),
                                ];
                                newEdu[index] = {
                                  ...newEdu[index],
                                  location: e.target.value,
                                };
                                setEditedProfile((prev) => ({
                                  ...prev,
                                  education: newEdu,
                                }));
                              }}
                              className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <p className="text-gray-600">
                              {edu.location || "N/A"}
                            </p>
                          )}
                        </div>
                      </div>
                      {isEditing ? (
                        <div className="mt-3">
                          <label className="text-xs font-medium text-gray-500">
                            Description / वर्णन
                          </label>
                          <textarea
                            value={
                              editedProfile?.education?.[index]?.description ||
                              ""
                            }
                            onChange={(e) => {
                              const newEdu = [
                                ...(editedProfile.education || []),
                              ];
                              newEdu[index] = {
                                ...newEdu[index],
                                description: e.target.value,
                              };
                              setEditedProfile((prev) => ({
                                ...prev,
                                education: newEdu,
                              }));
                            }}
                            rows={2}
                            className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      ) : (
                        edu.description && (
                          <p className="text-sm text-gray-700 mt-2">
                            {edu.description}
                          </p>
                        )
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skills */}
            {profile?.skills && profile.skills.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Skills <span className="text-gray-500">/ कौशल्ये</span>
                </h2>
                {isEditing ? (
                  <div>
                    <input
                      type="text"
                      value={editedProfile?.skills?.join(", ") || ""}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          skills: e.target.value
                            .split(",")
                            .map((s) => s.trim())
                            .filter((s) => s),
                        }))
                      }
                      placeholder="Enter skills separated by commas"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Separate skills with commas
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Job Preferences */}
            {profile?.preferences && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Job Preferences{" "}
                  <span className="text-gray-500">/ नोकरी प्राधान्ये</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <BilingualLabel
                      english="Career Level"
                      marathi="करिअर स्तर"
                      className="block mb-1"
                    />
                    {isEditing ? (
                      <select
                        value={editedProfile?.preferences?.careerLevel || ""}
                        onChange={(e) =>
                          setEditedProfile((prev) => ({
                            ...prev,
                            preferences: {
                              ...prev.preferences,
                              careerLevel: e.target.value,
                            },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select level</option>
                        <option value="entry">Entry Level</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="executive">Executive</option>
                        <option value="senior">Senior</option>
                      </select>
                    ) : (
                      <p className="text-gray-900">
                        {profile.preferences?.careerLevel || "N/A"}
                      </p>
                    )}
                  </div>
                  <div>
                    <BilingualLabel
                      english="Notice Period"
                      marathi="नोटीस कालावधी"
                      className="block mb-1"
                    />
                    {isEditing ? (
                      <select
                        value={editedProfile?.preferences?.noticePeriod || ""}
                        onChange={(e) =>
                          setEditedProfile((prev) => ({
                            ...prev,
                            preferences: {
                              ...prev.preferences,
                              noticePeriod: e.target.value,
                            },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select period</option>
                        <option value="Immediate">Immediate</option>
                        <option value="15 days">15 days</option>
                        <option value="1 month">1 month</option>
                        <option value="2 months">2 months</option>
                        <option value="3 months">3 months</option>
                      </select>
                    ) : (
                      <p className="text-gray-900">
                        {profile.preferences?.noticePeriod || "N/A"}
                      </p>
                    )}
                  </div>
                  <div>
                    <BilingualLabel
                      english="Min Salary (₹/mo)"
                      marathi="किमान पगार (₹/महिना)"
                      className="block mb-1"
                    />
                    {isEditing ? (
                      <input
                        type="text"
                        value={
                          editedProfile?.preferences?.expectedSalary?.min || ""
                        }
                        onChange={(e) =>
                          setEditedProfile((prev) => ({
                            ...prev,
                            preferences: {
                              ...prev.preferences,
                              expectedSalary: {
                                ...prev.preferences?.expectedSalary,
                                min: e.target.value,
                              },
                            },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-gray-900">
                        ₹{profile.preferences?.expectedSalary?.min || "N/A"}
                      </p>
                    )}
                  </div>
                  <div>
                    <BilingualLabel
                      english="Max Salary (₹/mo)"
                      marathi="कमाल पगार (₹/महिना)"
                      className="block mb-1"
                    />
                    {isEditing ? (
                      <input
                        type="text"
                        value={
                          editedProfile?.preferences?.expectedSalary?.max || ""
                        }
                        onChange={(e) =>
                          setEditedProfile((prev) => ({
                            ...prev,
                            preferences: {
                              ...prev.preferences,
                              expectedSalary: {
                                ...prev.preferences?.expectedSalary,
                                max: e.target.value,
                              },
                            },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-gray-900">
                        ₹{profile.preferences?.expectedSalary?.max || "N/A"}
                      </p>
                    )}
                  </div>
                  <div>
                    <BilingualLabel
                      english="Preferred Locations"
                      marathi="पसंतीची स्थाने"
                      className="block mb-1"
                    />
                    {isEditing ? (
                      <input
                        type="text"
                        value={
                          editedProfile?.preferences?.preferredLocations || ""
                        }
                        onChange={(e) =>
                          setEditedProfile((prev) => ({
                            ...prev,
                            preferences: {
                              ...prev.preferences,
                              preferredLocations: e.target.value,
                            },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-gray-900">
                        {profile.preferences?.preferredLocations || "N/A"}
                      </p>
                    )}
                  </div>
                  <div>
                    <BilingualLabel
                      english="Willing to Relocate"
                      marathi="स्थलांतर करण्यास तयार"
                      className="block mb-1"
                    />
                    {isEditing ? (
                      <select
                        value={
                          editedProfile?.preferences?.willingToRelocate
                            ? "true"
                            : "false"
                        }
                        onChange={(e) =>
                          setEditedProfile((prev) => ({
                            ...prev,
                            preferences: {
                              ...prev.preferences,
                              willingToRelocate: e.target.value === "true",
                            },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    ) : (
                      <p className="text-gray-900">
                        {profile.preferences?.willingToRelocate ? "Yes" : "No"}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <BilingualLabel
                      english="Job Types"
                      marathi="नोकरीचे प्रकार"
                      className="block mb-1"
                    />
                    {isEditing ? (
                      <input
                        type="text"
                        value={
                          editedProfile?.preferences?.jobTypes?.join(", ") || ""
                        }
                        onChange={(e) =>
                          setEditedProfile((prev) => ({
                            ...prev,
                            preferences: {
                              ...prev.preferences,
                              jobTypes: e.target.value
                                .split(",")
                                .map((s) => s.trim()),
                            },
                          }))
                        }
                        placeholder="Full-time, Part-time, Freelance"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-gray-900">
                        {profile.preferences?.jobTypes?.join(", ") || "N/A"}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <BilingualLabel
                      english="Work Modes"
                      marathi="कामाचे प्रकार"
                      className="block mb-1"
                    />
                    {isEditing ? (
                      <input
                        type="text"
                        value={
                          editedProfile?.preferences?.workModes?.join(", ") ||
                          ""
                        }
                        onChange={(e) =>
                          setEditedProfile((prev) => ({
                            ...prev,
                            preferences: {
                              ...prev.preferences,
                              workModes: e.target.value
                                .split(",")
                                .map((s) => s.trim()),
                            },
                          }))
                        }
                        placeholder="Remote, On-site, Hybrid"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-gray-900">
                        {profile.preferences?.workModes?.join(", ") || "N/A"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Employer Profile */}
        {userData.userType === "employer" && profile && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Company Information{" "}
                <span className="text-gray-500">/ कंपनी माहिती</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <BilingualLabel
                    english="Company Name"
                    marathi="कंपनीचे नाव"
                  />
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.companyName || ""}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          companyName: e.target.value,
                        }))
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.companyName || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <BilingualLabel english="Industry" marathi="उद्योग" />
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.industry || ""}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          industry: e.target.value,
                        }))
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.industry || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <BilingualLabel
                    english="Company Size"
                    marathi="कंपनीचा आकार"
                  />
                  {isEditing ? (
                    <select
                      value={editedProfile?.companySize || ""}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          companySize: e.target.value,
                        }))
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="501-1000">501-1000 employees</option>
                      <option value="1000+">1000+ employees</option>
                    </select>
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.companySize || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <BilingualLabel english="Location" marathi="स्थान" />
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.location || ""}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          location: e.target.value,
                        }))
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.location || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <BilingualLabel english="Website" marathi="वेबसाइट" />
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.website || ""}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          website: e.target.value,
                        }))
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.website || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <BilingualLabel
                    english="Founded Year"
                    marathi="स्थापना वर्ष"
                  />
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.foundedYear || ""}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          foundedYear: e.target.value,
                        }))
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.foundedYear || "N/A"}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <BilingualLabel english="Description" marathi="वर्णन" />
                  {isEditing ? (
                    <textarea
                      value={editedProfile?.description || ""}
                      onChange={(e) =>
                        setEditedProfile((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      rows={4}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {profile.description || "N/A"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamUsers;
